/**
 * `/help/platform-map` — how the platform is connected.
 *
 * The port of the demo's Help "map" tab (demo line 3902): the six-lane diagram
 * `helpMap()` drew, the three cards that explain how to read it, and the
 * seven-section page map `helpNav()` drew with its role dots.
 *
 * ## Declared deviation: lanes, not a fixed SVG
 *
 * The demo's map is a 1180 × 760 SVG with every box positioned by hand. At
 * 1280 px it is scaled down until the ten module labels are unreadable, and on
 * a phone it is unusable. The port keeps the structure — six lanes, bottom to
 * top, every box a link, the arrow that closes the circle — and lays it out as
 * a responsive stack of lanes instead. Nothing is lost but the coordinates.
 *
 * ## Declared omissions
 *
 * - **Live figures on the tower and module boxes.** The demo printed this
 *   morning's eight tower measures and each module's open-job count on the
 *   map. Both need several lazy domains, and both are one click away on the
 *   pages the boxes link to; the map carries the structure and the step counts.
 * - **The 17-stop guided tour**, which was the third Help tab. Tier 3.
 *
 * @see src/versions/v1/modules/help/PlatformMapScene.transform.ts
 * @see docs/logic/help/platform-map.md
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, ChevronRight, RotateCcw } from 'lucide-react';
import { HelpAnchor, SurfacePageBody, SurfacePageHeader, SurfacePageLayout } from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import { HelpTabs } from './HelpTabs';
import {
  helpStats,
  mapLanes,
  READING_CARDS,
  RETURN_CAPTION,
  roleColour,
  roleLabel,
  sectionColumns,
  type MapLane,
} from './PlatformMapScene.transform';

/** Demo line 3960 — the Help hero standfirst, verbatim. */
const STANDFIRST =
  'Everything in the platform hangs off one idea: the systems keep producing what they already ' +
  'produce, one store holds it, agents read it, and every machine and job moves through the ' +
  'same ten modules with five loops feeding the front of the model again. This page shows that ' +
  'shape, how to move around it, and which part of it belongs to whom.';

const COLUMN_CLASS: Readonly<Record<MapLane['columns'], string>> = {
  2: 'grid-cols-1',
  4: 'grid-cols-2 @2xl:grid-cols-4',
  5: 'grid-cols-2 @2xl:grid-cols-3 @4xl:grid-cols-5',
  8: 'grid-cols-2 @xl:grid-cols-4 @4xl:grid-cols-8',
  10: 'grid-cols-2 @xl:grid-cols-4 @3xl:grid-cols-5 @5xl:grid-cols-10',
};

export function PlatformMapScene() {
  const lanes = useMemo(() => mapLanes(), []);
  const sections = useMemo(() => sectionColumns(), []);
  const stats = useMemo(() => helpStats(), []);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="PlatformMapScene">
        <SurfacePageBody className="@container max-w-[1500px] space-y-8" as="div">
          <SurfacePageHeader
            kicker="Help"
            title="How the platform is connected"
            description={STANDFIRST}
            actions={<HelpAnchor doc="help/platform-map" />}
            tabs={<HelpTabs current="map" />}
          />

          <ul className="flex flex-wrap gap-x-8 gap-y-3">
            {stats.map((stat) => (
              <li key={stat.eyebrow}>
                <span
                  className="block text-[10px] font-semibold uppercase tracking-[0.1em]"
                  style={{ color: 'var(--ink-tertiary)' }}
                >
                  {stat.eyebrow}
                </span>
                <span
                  className="mono block text-[22px] font-semibold leading-tight"
                  style={{ color: 'var(--ink-primary)' }}
                >
                  {stat.value}
                </span>
                <span className="block text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
                  {stat.detail}
                </span>
              </li>
            ))}
          </ul>

          <Commentable name="PlatformLayers">
            <section className="space-y-2.5">
              <p className="text-[12px]" style={{ color: 'var(--ink-tertiary)' }}>
                Bottom to top: the systems it reads, one store, the agents, the ten modules, the
                loops that feed the front again, and the control tower over all of it. Every box
                opens the page behind it.
              </p>

              {[...lanes].reverse().map((lane, i, all) => (
                <div key={lane.id}>
                  <div
                    className="rounded-xl border p-3"
                    style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                  >
                    <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
                      <h2
                        className="text-[10px] font-semibold uppercase tracking-[0.1em]"
                        style={{ color: 'var(--ink-secondary)' }}
                      >
                        {lane.title}
                      </h2>
                      <span className="text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
                        {lane.subtitle}
                      </span>
                    </div>

                    <div className={cn('grid gap-2', COLUMN_CLASS[lane.columns])}>
                      {lane.tiles.map((tile) =>
                        tile.to ? (
                          <Link
                            key={tile.id}
                            to={tile.to}
                            className="flex min-w-0 flex-col gap-0.5 rounded-lg border px-2.5 py-2 transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                            style={{
                              borderColor: 'var(--hairline)',
                              borderLeft: tile.colour
                                ? `3px solid ${tile.colour}`
                                : '1px solid var(--hairline)',
                            }}
                          >
                            {tile.index ? (
                              <span
                                className="mono text-[9.5px] font-semibold"
                                style={{ color: 'var(--ink-tertiary)' }}
                              >
                                {tile.index}
                              </span>
                            ) : null}
                            <span
                              className="truncate text-[11.5px] font-medium leading-snug"
                              style={{ color: 'var(--ink-primary)' }}
                              title={tile.label}
                            >
                              {tile.label}
                            </span>
                            {tile.detail ? (
                              <span
                                className="text-[10.5px] leading-snug"
                                style={{ color: 'var(--ink-tertiary)' }}
                              >
                                {tile.detail}
                              </span>
                            ) : null}
                          </Link>
                        ) : null,
                      )}
                    </div>
                  </div>

                  {i < all.length - 1 ? (
                    <div
                      className="flex items-center justify-center gap-1.5 py-1 text-[10.5px]"
                      style={{ color: 'var(--ink-tertiary)' }}
                    >
                      {lane.id === 'loops' ? (
                        <>
                          <RotateCcw className="h-3 w-3" aria-hidden="true" />
                          <span>{RETURN_CAPTION}</span>
                        </>
                      ) : (
                        <ArrowUp className="h-3 w-3" aria-hidden="true" />
                      )}
                    </div>
                  ) : null}
                </div>
              ))}
            </section>
          </Commentable>

          <Commentable name="PlatformReading">
            <div className="grid grid-cols-1 gap-3 @3xl:grid-cols-3">
              {READING_CARDS.map((card) => (
                <section
                  key={card.title}
                  className="rounded-xl border p-4"
                  style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                >
                  <h2
                    className="text-[10px] font-semibold uppercase tracking-[0.1em]"
                    style={{ color: 'var(--ink-tertiary)' }}
                  >
                    {card.title}
                  </h2>
                  <dl className="mt-2.5 space-y-2.5">
                    {card.rows.map((row) => (
                      <div key={row.heading}>
                        <dt
                          className="text-[12px] font-semibold"
                          style={{ color: 'var(--ink-primary)' }}
                        >
                          {row.heading}
                        </dt>
                        <dd
                          className="mt-0.5 text-[11.5px] leading-[1.55]"
                          style={{ color: 'var(--ink-secondary)' }}
                        >
                          {row.body}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>
              ))}
            </div>
          </Commentable>

          <Commentable name="PlatformNavMap">
            <section className="space-y-3">
              <div className="flex flex-wrap items-baseline gap-x-3">
                <h2
                  className="text-[15px] font-semibold tracking-tight"
                  style={{ color: 'var(--ink-primary)' }}
                >
                  The seven sections
                </h2>
                <p className="text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
                  the menu on the left, and what each page answers — the coloured dots are the
                  roles who live there
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-2 @5xl:grid-cols-4">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <section
                      key={section.key}
                      className="flex min-w-0 flex-col gap-2 rounded-xl border p-3"
                      style={{
                        borderColor: 'var(--hairline)',
                        background: 'var(--surface-2)',
                        borderTop: `3px solid ${section.colour}`,
                      }}
                    >
                      {section.to ? (
                        <Link
                          to={section.to}
                          className="group flex items-center gap-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                        >
                          {Icon ? (
                            <Icon
                              className="h-4 w-4 shrink-0"
                              style={{ color: section.colour }}
                              aria-hidden="true"
                            />
                          ) : null}
                          <b
                            className="min-w-0 flex-1 truncate text-[13px] font-semibold"
                            style={{ color: 'var(--ink-primary)' }}
                          >
                            {section.name}
                          </b>
                          <ChevronRight
                            className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5"
                            style={{ color: 'var(--ink-tertiary)' }}
                            aria-hidden="true"
                          />
                        </Link>
                      ) : null}

                      <p className="text-[11px] leading-snug" style={{ color: 'var(--ink-secondary)' }}>
                        {section.what}
                      </p>

                      <ul className="flex flex-wrap gap-1">
                        {section.who.map((role) => (
                          <li
                            key={role}
                            className="rounded-full px-1.5 py-px text-[9.5px] font-medium"
                            style={{
                              color: roleColour(role),
                              background: `color-mix(in srgb, ${roleColour(role)} 14%, transparent)`,
                            }}
                          >
                            {roleLabel(role)}
                          </li>
                        ))}
                      </ul>

                      <ul className="flex flex-col gap-1">
                        {section.pages.map((page) =>
                          page.to ? (
                            <li key={page.title}>
                              <Link
                                to={page.to}
                                className="block rounded-lg border px-2.5 py-1.5 transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                                style={{ borderColor: 'var(--hairline)' }}
                              >
                                <span className="flex items-center gap-1.5">
                                  <span
                                    className="min-w-0 flex-1 truncate text-[11.5px] font-medium"
                                    style={{ color: 'var(--ink-primary)' }}
                                  >
                                    {page.title}
                                  </span>
                                  <span className="flex shrink-0 gap-0.5" aria-hidden="true">
                                    {page.roles.map((role) => (
                                      <i
                                        key={role}
                                        title={roleLabel(role)}
                                        className="inline-block h-1.5 w-1.5 rounded-full"
                                        style={{ background: roleColour(role) }}
                                      />
                                    ))}
                                  </span>
                                </span>
                                <span
                                  className="mt-0.5 block text-[10.5px] leading-snug"
                                  style={{ color: 'var(--ink-tertiary)' }}
                                >
                                  {page.question}
                                </span>
                              </Link>
                            </li>
                          ) : null,
                        )}
                      </ul>
                    </section>
                  );
                })}
              </div>
            </section>
          </Commentable>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
